/**
 * @file useDesigns.ts
 * @description Библиотека дизайнов в конструкторе (phase19.16 Р1-6а, §7.9
 * фазы; uiux-builder-shell.md §9; контракт §14.2; решения 2026-10-12):
 * список, сохранение текущего вида, «Создать новый» (пустой шаблон или
 * копия — сразу в черновик), «Обновить из текущего вида», переименование,
 * удаление и «Применить ко всему сайту» — в черновик, без публикации.
 *
 * Перед применением, созданием и «сохранить/обновить из текущего вида»
 * автосохранение оформления и бренда доводится до сервера (`settle`);
 * версии — из его итога. После применения части THEME и BRAND черновика
 * обновляются в общем состоянии каркаса (`applyPart`) — канва показывает
 * дизайн сразу. Список после любой правки (и после ошибки) перечитывается
 * с сервера (одна истина — сервер).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useSiteView } from "../../components/site-view/site-view-context";
import {
  siteDesigns,
  type ApplySiteDesignResult,
  type CreatedSiteDesign,
  type CreateSiteDesignInput,
  type SiteDesignFrom,
  type SiteDesignList,
} from "../../lib/api-site-designs";
import { designErrorText, DraftNotSavedError } from "../../lib/site-view/design-errors";

// Прежние импорты из хука продолжают работать.
export { designErrorText, DraftNotSavedError };

/** «Создать новый»: имя, источник и (если нужна) страховочная копия черновика. */
export interface CreateNewDesignInput {
  name: string;
  from: SiteDesignFrom;
  /** Сначала сохранить нынешний черновик дизайном с этим именем; `null` — не нужно. */
  draftCopyName: string | null;
}

/** «Обновить из текущего вида»: логотипы и (если нужна) копия вида с сайта. */
export interface UpdateDesignInput {
  withLogos: boolean;
  /** Сначала сохранить опубликованный вид дизайном с этим именем; `null` — не нужно. */
  publishedCopyName: string | null;
}

/** Колбэк «автосохранение дошло до сервера» — окно меняет подпись кнопки. */
type OnSettled = () => void;

export interface DesignsApi {
  data: SiteDesignList | null;
  error: string | null;
  reload: () => Promise<void>;
  create: (input: CreateSiteDesignInput) => Promise<void>;
  /** Создать и сразу применить к черновику; результат — дизайн и что не перенеслось. */
  createNew: (input: CreateNewDesignInput, onSettled?: OnSettled) => Promise<CreatedSiteDesign>;
  updateFromDraft: (id: string, input: UpdateDesignInput, onSettled?: OnSettled) => Promise<void>;
  rename: (id: string, name: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  /** Применить ко всему сайту (в черновик); результат — что не перенеслось. */
  apply: (id: string) => Promise<ApplySiteDesignResult>;
}

export function useDesigns(enabled: boolean): DesignsApi {
  const view = useSiteView();
  const [data, setData] = useState<SiteDesignList | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setData(await siteDesigns.list());
      setError(null);
    } catch (err) {
      setError(designErrorText(err, "Не удалось загрузить дизайны. Вид сайта не изменился."));
    }
  }, []);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  // Свежие части черновика — после окна подтверждения, не из замыкания.
  const latest = useRef(view);
  latest.current = view;

  /** Дождаться автосохранения оформления и бренда; итог — версии частей. */
  const settleDraft = useCallback(async (): Promise<{ THEME: number; BRAND: number }> => {
    const { parts, draft } = latest.current;
    const theme = parts ? await parts.theme.settle() : (draft?.parts.THEME.version ?? 0);
    const brand = parts ? await parts.brand.settle() : (draft?.parts.BRAND.version ?? 0);
    if (theme === null || brand === null) throw new DraftNotSavedError();
    return { THEME: theme, BRAND: brand };
  }, []);

  /** Выполнить правку, затем перечитать список — и при ошибке (страховочная копия могла появиться). */
  const thenReload = useCallback(
    async <T,>(run: () => Promise<T>): Promise<T> => {
      try {
        return await run();
      } finally {
        await reload();
      }
    },
    [reload],
  );

  const { applyPart } = view;
  const applyResult = useCallback(
    (result: ApplySiteDesignResult) => {
      applyPart(result.parts.THEME, { theme: result.theme });
      applyPart(result.parts.BRAND, { brand: result.brand });
    },
    [applyPart],
  );

  const create = useCallback(
    async (input: CreateSiteDesignInput) => {
      if (input.source !== "PUBLISHED") await settleDraft();
      await thenReload(() => siteDesigns.create(input));
    },
    [thenReload, settleDraft],
  );

  const createNew = useCallback(
    async (input: CreateNewDesignInput, onSettled?: OnSettled) => {
      const versions = await settleDraft();
      onSettled?.();
      return thenReload(async () => {
        // Страховочная копия — текущий черновик целиком (с логотипами), до того как его заменит новый дизайн.
        if (input.draftCopyName) await siteDesigns.create({ name: input.draftCopyName, withLogos: true });
        const created = await siteDesigns.create({ name: input.name, from: input.from, apply: true, expectedVersions: versions });
        if (created.applied) applyResult(created.applied);
        return created;
      });
    },
    [applyResult, thenReload, settleDraft],
  );

  const updateFromDraft = useCallback(
    async (id: string, input: UpdateDesignInput, onSettled?: OnSettled) => {
      await settleDraft();
      onSettled?.();
      await thenReload(async () => {
        // Копия вида с сайта — полная (с логотипами), иначе она не воспроизводит вид.
        if (input.publishedCopyName) await siteDesigns.create({ name: input.publishedCopyName, source: "PUBLISHED", withLogos: true });
        await siteDesigns.updateFromDraft(id, input.withLogos);
      });
    },
    [thenReload, settleDraft],
  );

  const rename = useCallback((id: string, name: string) => thenReload(async () => void (await siteDesigns.rename(id, name))), [thenReload]);
  const remove = useCallback((id: string) => thenReload(() => siteDesigns.remove(id)), [thenReload]);

  const apply = useCallback(
    async (id: string) => {
      const versions = await settleDraft();
      const result = await siteDesigns.apply(id, versions);
      applyResult(result);
      await reload();
      return result;
    },
    [applyResult, reload, settleDraft],
  );

  return { data, error, reload, create, createNew, updateFromDraft, rename, remove, apply };
}
